import { logger } from '../utils/logger';
import { openaiService } from './openaiService';
import { supabase } from './supabaseService';
import { leadProbabilityService } from './leadProbabilityService';

const ANALYSIS_VERSION = 'lead_intelligence_v2';

interface LeadSnapshot {
  id: string;
  fullName: string;
  company: string;
  email: string;
  phone?: string;
  role?: string;
  source: string;
  status: string;
  productInterest: string;
  score: number;
  owner: string;
  notes?: string;
  createdAt: Date;
  lastContactDate?: Date;
  nextFollowUp?: Date;
}

async function persistFields(leadId: string, fields: Record<string, unknown>): Promise<void> {
  const { error } = await supabase
    .from('leads')
    .update(fields)
    .eq('id', leadId);

  if (error) {
    logger.error(`leadIntelligenceService: error persisting fields for lead ${leadId}`, error);
    throw error;
  }
}

async function runFullAnalysis(lead: LeadSnapshot): Promise<void> {
  const leadId = lead.id;

  try {
    await persistFields(leadId, {
      analysis_status: 'pending',
      analysis_version: ANALYSIS_VERSION
    });

    const stage1 = await openaiService.analyzeLeadStage1({
      fullName: lead.fullName,
      company: lead.company,
      email: lead.email,
      phone: lead.phone,
      role: lead.role,
      source: lead.source,
      status: lead.status,
      productInterest: lead.productInterest,
      score: lead.score,
      owner: lead.owner,
      notes: lead.notes,
      createdAt: lead.createdAt,
      lastContactDate: lead.lastContactDate,
      nextFollowUp: lead.nextFollowUp
    });

    await persistFields(leadId, {
      analysis_stage1_result: stage1.result,
      analysis_stage1_category: stage1.category,
      analysis_stage1_reason: stage1.reason
    });

    if (stage1.result === 'reject') {
      await persistFields(leadId, {
        analysis_status: 'completed',
        ai_analyzed_at: new Date().toISOString()
      });
      logger.info(`leadIntelligenceService: lead ${leadId} rejected at Stage 1 (${stage1.category})`);
      return;
    }

    const stage2 = await openaiService.analyzeLeadStage2({
      fullName: lead.fullName,
      company: lead.company,
      email: lead.email,
      phone: lead.phone,
      role: lead.role,
      source: lead.source,
      status: lead.status,
      productInterest: lead.productInterest,
      score: lead.score,
      owner: lead.owner,
      notes: lead.notes,
      createdAt: lead.createdAt,
      lastContactDate: lead.lastContactDate,
      nextFollowUp: lead.nextFollowUp
    });

    await persistFields(leadId, {
      project_line: stage2.projectLine,
      score: stage2.leadScore,
      lead_tier: stage2.leadTier,
      lead_score_breakdown: stage2.leadScoreBreakdown,
      lead_analysis_summary: stage2.leadAnalysisSummary,
      lead_red_flags: stage2.leadRedFlags,
      lead_pain_signals: stage2.leadPainSignals,
      lead_fit_signals: stage2.leadFitSignals,
      recommended_action: stage2.recommendedAction,
      pain_level: stage2.painLevel,
      opportunity_band: stage2.opportunityBand,
      analysis_status: 'completed',
      ai_analyzed_at: new Date().toISOString()
    });

    await leadProbabilityService.runProbabilityAnalysis(leadId, {
      id: leadId,
      leadTier: stage2.leadTier,
      projectLine: stage2.projectLine,
      painLevel: stage2.painLevel,
      leadRedFlags: stage2.leadRedFlags,
      leadPainSignals: stage2.leadPainSignals,
      leadFitSignals: stage2.leadFitSignals,
      leadScoreBreakdown: stage2.leadScoreBreakdown,
      notes: lead.notes,
      productInterest: lead.productInterest
    });

    logger.info(`leadIntelligenceService: lead ${leadId} fully analyzed — tier=${stage2.leadTier} score=${stage2.leadScore}`);
  } catch (error) {
    logger.error(`leadIntelligenceService: analysis failed for lead ${leadId}`, error);
    try {
      await persistFields(leadId, { analysis_status: 'failed' });
    } catch {
      // silent — best effort
    }
    try {
      const { systemHealthService } = await import('./systemHealthService');
      await systemHealthService.recordSystemEvent(
        'lead_analysis_failed',
        'ai',
        'warning',
        error instanceof Error ? error.message : String(error),
        { leadId }
      );
    } catch {
      // silent — best effort
    }
  }
}

function triggerOnCreate(lead: LeadSnapshot): void {
  void runFullAnalysis(lead);
}

async function rerunAnalysis(leadId: string): Promise<void> {
  const { data, error } = await supabase
    .from('leads')
    .select('*')
    .eq('id', leadId)
    .maybeSingle();

  if (error || !data) {
    logger.error(`leadIntelligenceService: cannot rerun — lead ${leadId} not found`, error);
    return;
  }

  const lead: LeadSnapshot = {
    id: data.id,
    fullName: data.full_name,
    company: data.company,
    email: data.email,
    phone: data.phone,
    role: data.role,
    source: data.source,
    status: data.status,
    productInterest: data.product_interest,
    score: data.score ?? 0,
    owner: data.owner,
    notes: data.notes,
    createdAt: new Date(data.created_at),
    lastContactDate: data.last_contact_date ? new Date(data.last_contact_date) : undefined,
    nextFollowUp: data.next_follow_up ? new Date(data.next_follow_up) : undefined
  };

  void runFullAnalysis(lead);
}

export const leadIntelligenceService = {
  triggerOnCreate,
  rerunAnalysis,
  runFullAnalysis
};
